'use client'

import { useState, type ReactNode } from 'react'
import { Megaphone, ThumbsUp, Minus, ThumbsDown } from 'lucide-react'

type Mood = 'positive' | 'neutral' | 'negative'

// _paq.push([fn]) runs fn asynchronously once Matomo processes its queue, so the
// visitor ID must be awaited via callback rather than read back synchronously.
function getMatomoVisitorId(): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      if (typeof window === 'undefined' || !window._paq) {
        resolve(null)
        return
      }
      const timeout = setTimeout(() => resolve(null), 1000)
      window._paq.push([function (this: { getVisitorId: () => string }) {
        clearTimeout(timeout)
        resolve(this.getVisitorId())
      }])
    } catch {
      resolve(null)
    }
  })
}

const MOODS: { value: Mood; label: string; icon: ReactNode }[] = [
  { value: 'positive', label: 'Gut', icon: <ThumbsUp size={18} /> },
  { value: 'neutral', label: 'Ok', icon: <Minus size={18} /> },
  { value: 'negative', label: 'Schlecht', icon: <ThumbsDown size={18} /> },
]

export function FeedbackWidget() {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [mood, setMood] = useState<Mood | null>(null)
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!text.trim()) return
    setLoading(true)
    setError(null)

    try {
      const matomoVisitorId = await getMatomoVisitorId()
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text.trim(),
          mood,
          url: window.location.href,
          matomo_visitor_id: matomoVisitorId,
        }),
      })

      if (!res.ok) {
        throw new Error('Failed to send feedback')
      }

      setSent(true)
      setTimeout(() => {
        setOpen(false)
        setSent(false)
        setText('')
        setMood(null)
        setError(null)
      }, 2000)
    } catch {
      setError('Feedback konnte nicht gesendet werden. Bitte versuch es nochmal.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 w-11 h-11 bg-ink text-paper flex items-center justify-center hover:bg-signal transition-colors"
        aria-label="Feedback geben"
        aria-expanded={open}
      >
        <Megaphone size={18} aria-hidden="true" />
      </button>

      {open && (
        <div className="fixed bottom-16 right-6 z-50 w-72 bg-paper border border-ink p-5">
          {sent ? (
            <div className="py-2">
              <p className="font-mono text-xs uppercase tracking-widest text-ink/50 mb-2">Gesendet</p>
              <p className="text-sm text-ink">Danke für dein Feedback!</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <p className="font-mono text-xs uppercase tracking-widest text-ink/50 mb-4">Feedback</p>

              <div className="flex gap-2 mb-4">
                {MOODS.map(({ value, label, icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setMood(mood === value ? null : value)}
                    className={`flex-1 flex flex-col items-center gap-1.5 py-2.5 border text-xs font-mono uppercase tracking-wider transition-colors ${
                      mood === value
                        ? 'bg-ink text-paper border-ink'
                        : 'border-ink/30 text-ink/50 hover:border-ink hover:text-ink'
                    }`}
                  >
                    {icon}
                    {label}
                  </button>
                ))}
              </div>

              <textarea
                value={text}
                onChange={(e) => {
                  setText(e.target.value)
                  setError(null)
                }}
                placeholder="Was ist dir aufgefallen?"
                rows={4}
                className="w-full border border-ink/30 p-3 text-sm resize-none bg-paper text-ink
                           focus:border-ink placeholder:text-ink/30 transition-colors"
                required
              />

              {error && (
                <p role="alert" className="mt-2 text-xs font-mono text-signal uppercase tracking-widest">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={!text.trim() || loading}
                className={`mt-3 w-full py-2 text-xs font-mono uppercase tracking-widest transition-colors ${
                  text.trim() && !loading
                    ? 'bg-ink text-paper hover:bg-signal cursor-pointer'
                    : 'border border-ink/20 text-ink/30 cursor-not-allowed'
                }`}
              >
                {loading ? 'Wird gesendet …' : 'Senden'}
              </button>
            </form>
          )}
        </div>
      )}
    </>
  )
}
